/**
 * 판매장부 정산현황 원본 캐시 (IndexedDB)
 *
 * 요약장부에서 월을 눌러 상세를 볼 때마다 그 기간의 정산현황 원본을 서버에서 받아온다.
 * 월을 오가며 눌러보면 같은 데이터를 반복해서 내려받는데, 한 달치가 큰 회사 기준
 * 1만 건이 넘고 압축 미들웨어도 없어서 왕복 비용이 크다.
 * 그래서 조회 기간 단위로 담아두고, 같은 기간을 다시 열면 네트워크를 타지 않는다.
 *
 * 요약장부의 연간 집계('summary-YYYY')도 같이 담는다. 서버 집계가 상품 수에 비례해
 * 1~3초 걸리는데, 화면을 오갈 때마다(keep-alive 복귀) 다시 부르면 매번 그만큼 기다린다.
 *
 * 캐시가 낡지 않게 하는 장치가 셋이다.
 *  1) stamp — 서버가 알려주는 '이 회사 장부 원본이 마지막으로 바뀐 시각'. 담을 때와 다르면 버린다.
 *     한 계정을 여럿이 보는데 그중 한 명만 수집을 돌리면, 나머지는 자기 브라우저 캐시만 보고
 *     옛 숫자를 계속 봐 왔다. stamp 가 그걸 막는다.
 *  2) TTL — stamp 를 못 받는 예외 상황의 안전망. stamp 가 제 역할을 하므로 길게 잡는다.
 *  3) 버리기 — 수집·잠금처럼 그 계정 일이면 clearScope(계정), 로그아웃이면 clearAll().
 *
 * 서버 계산식을 고쳤을 때는 옛 숫자가 남으므로 SCHEMA를 올려야 한다.
 */

const DB_NAME = 'couplus-ledger';
const DB_VERSION = 1;
const STORE = 'monthly';
// 데이터가 바뀌면 stamp 가 반드시 갱신되므로(server.js 의 DATA_WRITE_PATHS 미들웨어)
// 길게 잡아도 낡은 값을 볼 일이 없다. stamp 를 못 받는 예외 상황의 안전망이다
const TTL_MS = 12 * 60 * 60 * 1000;   // 12시간

// 저장 형식이 바뀌면 올린다. 옛 형식이 그대로 맞아버리면 화면이 빈 채로 뜬다
// v7: 키를 companyid → 계정(_id) 기준으로 바꾸고 stamp 비교를 엄격하게 함
// v8: 월별 집계에 기타비용(상품 직접비)이 서버에서 반영되도록 계산식이 바뀜
const SCHEMA = 'v8';

let dbPromise = null;

function openDB() {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
        if (typeof indexedDB === 'undefined') {
            reject(new Error('IndexedDB 미지원'));
            return;
        }
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE)) {
                db.createObjectStore(STORE, { keyPath: 'key' });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
    return dbPromise;
}

function tx(mode) {
    return openDB().then(db => db.transaction(STORE, mode).objectStore(STORE));
}

// scope = 계정 식별자(user._id). companyid 로 잡으면 안 된다 —
// 같은 companyid 문자열을 쓰는 서로 다른 계정이 실제로 700 묶음 넘게 있고,
// 값이 비었을 때 'me' 같은 공용 키로 떨어지면 공용 PC에서 앞사람 숫자를 본다.
function keyOf(scope, month) {
    return `${SCHEMA}|${scope}|${month}`;
}

/**
 * 캐시에서 월 상세를 꺼낸다. 없거나 만료됐으면 null.
 * @param {string} scope 계정 식별자(user._id). 비어 있으면 캐시를 쓰지 않는다
 * @param {string} month 'YYYY-MM'
 * @param {string} [stamp] 서버가 준 원본 변경 시각. 저장 때와 다르면 버린다
 */
export async function getMonth(scope, month, stamp) {
    if (!scope) return null;   // 누구 것인지 모르면 캐시를 쓰지 않는다
    try {
        const store = await tx('readonly');
        const row = await new Promise((resolve, reject) => {
            const r = store.get(keyOf(scope, month));
            r.onsuccess = () => resolve(r.result || null);
            r.onerror = () => reject(r.error);
        });
        if (!row) return null;
        if (Date.now() - row.savedAt > TTL_MS) return null;
        // stamp 는 양쪽이 정확히 같을 때만 통과시킨다.
        // '담을 땐 stamp 가 없었다' 는 경우도 못 믿으므로 버린다
        if ((row.stamp || '') !== (stamp || '')) return null;
        return row.data;
    } catch (e) {
        console.warn('[ledgerCache] 읽기 실패 — 서버에서 받습니다', e.message);
        return null;
    }
}

export async function putMonth(scope, month, data, stamp) {
    if (!scope) return;
    try {
        await new Promise((resolve, reject) => {
            openDB().then(db => {
                const t = db.transaction(STORE, 'readwrite');
                t.objectStore(STORE).put({
                    key: keyOf(scope, month),
                    scope,
                    month,
                    data,
                    stamp: stamp || '',
                    savedAt: Date.now()
                });
                t.oncomplete = () => resolve();
                t.onerror = () => reject(t.error);
                t.onabort = () => reject(t.error || new Error('저장 중단'));
            }).catch(reject);
        });
        await prune();
    } catch (e) {
        // 자리가 없으면 통째로 비운다. 다시 받을 수 있는 값이라 버려도 잃는 게 없다
        if (e && (e.name === 'QuotaExceededError' || /quota/i.test(e.message || ''))) {
            console.warn('[ledgerCache] 저장 공간이 부족해 캐시를 비웁니다');
            await clearAll();
            return;
        }
        console.warn('[ledgerCache] 저장 실패 (무시)', e.message);
    }
}

// 계정·기간마다 항목이 늘어난다. 큰 회사는 한 달치가 6~8MB 라 그대로 두면 계속 쌓인다.
// 이미 만료된 것(TTL 초과)은 어차피 못 쓰므로 우선 버리고, 그래도 많으면 오래된 것부터 버린다
const MAX_ENTRIES = 120;

async function prune() {
    try {
        const store = await tx('readonly');
        const rows = await new Promise((resolve, reject) => {
            const r = store.getAll();
            r.onsuccess = () => resolve(r.result || []);
            r.onerror = () => reject(r.error);
        });
        const now = Date.now();
        const 버릴것 = rows
            .filter(r => now - r.savedAt > TTL_MS)
            .concat(
                rows.filter(r => now - r.savedAt <= TTL_MS)
                    .sort((a, b) => b.savedAt - a.savedAt)
                    .slice(MAX_ENTRIES)
            );
        if (!버릴것.length) return;
        const del = await tx('readwrite');
        버릴것.forEach(r => del.delete(r.key));
        console.log('[ledgerCache] 오래된 캐시 정리', 버릴것.length, '건');
    } catch (e) {
        console.warn('[ledgerCache] 정리 실패 (무시)', e.message);
    }
}

/** 수집 후처럼 데이터가 바뀌었을 때, 그리고 로그인/로그아웃 때 전부 버린다 */
export async function clearAll() {
    try {
        const store = await tx('readwrite');
        store.clear();
        console.log('[ledgerCache] 캐시 비움');
    } catch (e) {
        console.warn('[ledgerCache] 비우기 실패 (무시)', e.message);
    }
}

/**
 * 한 계정 것만 버린다. 수집이나 잠금처럼 '그 계정에서 일어난 일'에는 이걸 쓴다 —
 * clearAll() 을 쓰면 옆 계정이 받아둔 것까지 날아가 전환할 때마다 다시 받게 된다.
 */
export async function clearScope(scope) {
    if (!scope) return;
    try {
        const store = await tx('readwrite');
        const prefix = `${SCHEMA}|${scope}|`;
        const range = IDBKeyRange.bound(prefix, prefix + '￿');
        await new Promise((resolve, reject) => {
            const r = store.openCursor(range);
            r.onsuccess = () => {
                const cur = r.result;
                if (!cur) return resolve();
                cur.delete();
                cur.continue();
            };
            r.onerror = () => reject(r.error);
        });
        console.log('[ledgerCache] 캐시 비움 (이 계정만)');
    } catch (e) {
        console.warn('[ledgerCache] 비우기 실패 (무시)', e.message);
    }
}

/** 특정 달만 버린다 (그 달 비용을 고쳤을 때 등) */
export async function clearMonth(scope, month) {
    if (!scope) return;
    try {
        const store = await tx('readwrite');
        store.delete(keyOf(scope, month));
    } catch (e) {
        console.warn('[ledgerCache] 삭제 실패 (무시)', e.message);
    }
}

export default { getMonth, putMonth, clearAll, clearScope, clearMonth };
